import React, { Fragment, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { ledgerList, voucherCreate } from "../../../services/accounts";

const VOUCHER_TYPE_MAP = {
  receiptvoucher: { label: "Receipt Voucher", type: "Receipt" },
  paymentvoucher: { label: "Payment Voucher", type: "Payment" },
  journalvoucher: { label: "Journal Voucher", type: "Journal" },
  contravoucher:  { label: "Contra Voucher",  type: "Contra"  },
};

const emptyRow = { ledger_id: "", debit_amount: "", credit_amount: "" };

export default function VoucherEntry() {
  const location    = useLocation();
  const navigate    = useNavigate();
  const urlSlug     = location.pathname.split("/").pop();
  const voucherMeta = VOUCHER_TYPE_MAP[urlSlug] || { label: "Voucher Entry", type: "Journal" };

  const [ledgerOptions, setLedgerOptions] = useState([]);
  const [voucherDate, setVoucherDate]     = useState(moment().format("YYYY-MM-DD"));
  const [narration, setNarration]         = useState("");
  const [rows, setRows]                   = useState([{ ...emptyRow }, { ...emptyRow }]);
  const [errorMessage, setErrorMessage]   = useState("");
  const [loading, setLoading]             = useState(false);

  useEffect(() => {
    const temp = new FormData();
    if (localStorage.getItem("Role") === "1") {
      temp.append("academy_id", localStorage.getItem("AAcademy") || "");
    }
    ledgerList(temp).then((res) => {
      if (res?.status) {
        setLedgerOptions(res.data.map((l) => ({ value: l.id, label: l.ledger_name })));
      }
    });
  }, []);

  function addRow() {
    setRows((prev) => [...prev, { ...emptyRow }]);
  }

  function removeRow(index) {
    if (rows.length <= 2) return;
    setRows((prev) => prev.filter((_, i) => i !== index));
  }

  function updateRow(index, field, value) {
    setRows((prev) => prev.map((r, i) => i === index ? { ...r, [field]: value } : r));
  }

  const totalDebit  = rows.reduce((s, r) => s + (parseFloat(r.debit_amount)  || 0), 0);
  const totalCredit = rows.reduce((s, r) => s + (parseFloat(r.credit_amount) || 0), 0);
  const isBalanced  = Math.abs(totalDebit - totalCredit) < 0.01 && totalDebit > 0;

  function handleSubmit() {
    setErrorMessage("");
    if (!voucherDate) { setErrorMessage("Please select voucher date"); return; }
    if (rows.some((r) => !r.ledger_id)) { setErrorMessage("Please select ledger for all rows"); return; }
    if (!isBalanced) {
      setErrorMessage(`Debit (${totalDebit.toFixed(2)}) and Credit (${totalCredit.toFixed(2)}) must be equal`);
      return;
    }

    const payload = {
      voucher_type: voucherMeta.type,
      voucher_date: voucherDate,
      narration,
      items: rows.map((r) => ({
        ledger_id:     r.ledger_id?.value || r.ledger_id,
        debit_amount:  parseFloat(r.debit_amount)  || 0,
        credit_amount: parseFloat(r.credit_amount) || 0,
      })),
    };

    setLoading(true);
    voucherCreate(payload).then((res) => {
      setLoading(false);
      if (res?.status) {
        toast.success(res.message);
        setRows([{ ...emptyRow }, { ...emptyRow }]);
        setNarration("");
        navigate("/accounts/voucher-list");
      } else {
        setErrorMessage(res?.message || "Something went wrong");
      }
    });
  }

  return (
    <Fragment>
      <div className="page-header reported-header">
        <div>
          <h2 className="main-content-title tx-24 mg-b-5">{voucherMeta.label}</h2>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a>Accounts</a></li>
            <li className="breadcrumb-item active" aria-current="page">{voucherMeta.label}</li>
          </ol>
        </div>
      </div>

      <div className="content-body">
        <div className="container-fluid">
          <div className="card custom-card mg-b-20">
            <div className="card-body">
              <Row className="row-sm mx-0">
                <Col xxl={4} lg={4} md={4}>
                  <div className="mb-3">
                    <label className="form-label">Voucher Date <span className="text-danger"> *</span></label>
                    <input
                      type="date"
                      className="form-control"
                      value={voucherDate}
                      onChange={(e) => setVoucherDate(e.target.value)}
                    />
                  </div>
                </Col>
                <Col xxl={8} lg={8} md={8}>
                  <div className="mb-3">
                    <label className="form-label">Narration</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter narration / description"
                      value={narration}
                      onChange={(e) => setNarration(e.target.value)}
                    />
                  </div>
                </Col>
              </Row>

              <div className="table-responsive mt-2">
                <table className="table table-bordered">
                  <thead style={{ backgroundColor: "#2d4ac6" }}>
                    <tr>
                      <th style={{ width: "42%", color: "#fff" }}>Ledger <span style={{ color: "#fff" }}>*</span></th>
                      <th style={{ width: "22%", color: "#fff" }}>Debit (Dr)</th>
                      <th style={{ width: "22%", color: "#fff" }}>Credit (Cr)</th>
                      <th style={{ width: "14%", color: "#fff" }} className="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => (
                      <tr key={i}>
                        <td>
                          <Select
                            className="default-select"
                            placeholder="Select Ledger"
                            options={ledgerOptions}
                            value={row.ledger_id || null}
                            onChange={(e) => updateRow(i, "ledger_id", e)}
                            isClearable
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-control"
                            placeholder="0.00"
                            value={row.debit_amount}
                            onChange={(e) => updateRow(i, "debit_amount", e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-control"
                            placeholder="0.00"
                            value={row.credit_amount}
                            onChange={(e) => updateRow(i, "credit_amount", e.target.value)}
                          />
                        </td>
                        <td className="text-center">
                          {rows.length > 2 && (
                            <a onClick={() => removeRow(i)} className="btn btn-sm btn-danger">
                              <i className="fa fa-times" />
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>
                        <a onClick={addRow} className="btn btn-sm btn-success">
                          <i className="fa fa-plus me-1" /> Add Row
                        </a>
                      </td>
                      <td><strong className={totalDebit > 0 ? "text-primary" : ""}>{totalDebit.toFixed(2)}</strong></td>
                      <td><strong className={totalCredit > 0 ? "text-primary" : ""}>{totalCredit.toFixed(2)}</strong></td>
                      <td className="text-center">
                        {isBalanced
                          ? <span className="badge bg-success">Balanced</span>
                          : <span className="badge bg-danger">Unbalanced</span>}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {errorMessage !== "" && <span className="text-danger">{errorMessage}</span>}

              <div className="mt-3">
                <a onClick={handleSubmit} className={`btn btn-success me-2 ${loading ? "disabled" : ""}`}>
                  {loading ? "Saving..." : "Save Voucher"}
                </a>
                <a onClick={() => navigate("/accounts/voucher-list")} className="btn btn-secondary">
                  Cancel
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
}
